import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { MsalAuthenticationTemplate } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import Dashboard from './pages/Dashboard'
import Customers from './pages/Customers'
import CustomerDetail from './pages/CustomerDetail'
import M365UserDetail from './pages/M365UserDetail'
import Usage from './pages/Usage'
import AzureBilling from './pages/AzureBilling'
import CustomerAzureBilling from './pages/CustomerAzureBilling'
import Layout from './components/Layout'
import SetupCustomers from './pages/SetupCustomers'
import SetupCustomerDetail from './pages/SetupCustomerDetail'
import SetupIntegrations from './pages/SetupIntegrations'
import AuditLogs from './pages/AuditLogs'
import AuthCallback from './pages/AuthCallback'
import UnauthorizedPage from './components/UnauthorizedPage'
import { RoleProvider, useRole } from './contexts/RoleContext'
import { ReactNode } from 'react'

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

// Route wrapper that checks if user is authorized (in Admin or User group)
function AuthorizedRoute({ children }: { children: ReactNode }) {
  const { isAuthorized, isLoading } = useRole()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAuthorized) {
    return <UnauthorizedPage />
  }

  return <>{children}</>
}

// Route wrapper that checks if user is admin
function AdminRoute({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading } = useRole()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAdmin) {
    // Redirect non-admins to dashboard
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function AppContent() {
  return (
    <RoleProvider>
      <AuthorizedRoute>
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:customerId" element={<CustomerDetail />} />
            <Route path="/customers/:customerId/users/:userId" element={<M365UserDetail />} />
            <Route path="/customers/:customerId/azure-billing" element={<CustomerAzureBilling />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/azure-billing" element={<AzureBilling />} />

            {/* Admin-only routes */}
            <Route path="/setup" element={<AdminRoute><Navigate to="/setup/customers" replace /></AdminRoute>} />
            <Route path="/setup/customers" element={<AdminRoute><SetupCustomers /></AdminRoute>} />
            <Route path="/setup/customers/:customerId" element={<AdminRoute><SetupCustomerDetail /></AdminRoute>} />
            <Route path="/setup/integrations" element={<AdminRoute><SetupIntegrations /></AdminRoute>} />
            <Route path="/audit-logs" element={<AdminRoute><AuditLogs /></AdminRoute>} />
          </Routes>
        </Layout>
      </AuthorizedRoute>
    </RoleProvider>
  )
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Auth callback route - outside MSAL protection for admin consent popup */}
        <Route path="/auth-callback" element={<AuthCallback />} />

        {/* Protected routes */}
        <Route path="/*" element={
          <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
            <AppContent />
          </MsalAuthenticationTemplate>
        } />
      </Routes>
    </Router>
  )
}

export default App
